/* Copyright (c) 2026 - Svayam Infoware Pvt. Ltd. All rights reserved. */
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AskCitation, AskConversationSummary, AskFeedback } from '../../features/document-ocr/document-ocr.models';
import { DocumentOcrService } from '../../features/document-ocr/document-ocr.service';
import { userFacingMessage } from '../errors/user-facing-error';
import { AuthService } from './auth.service';
import { RbacService } from './rbac.service';
import {
  ASK_AI_SESSION_KEY_LEGACY,
  ASK_AI_TURNS_KEY_LEGACY,
  sessionKeyForUser,
  turnsKeyForUser,
} from './ask-ai-storage';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  citations?: AskCitation[];
  isError?: boolean;
  /** Index into this chat's conversation_json — assistant messages only, and only ones that
   * actually made it to the DB (an error bubble never did, so never carries one). Needed to
   * target regenerate/feedback at the right turn. */
  turnIndex?: number;
  feedback?: AskFeedback | null;
}

export type ChatTurn = { que: string; ans: string; feedback?: AskFeedback };

const CASE_MARKER_RE = /\{\{CASE:([\d,\s]+)\}\}/g;

/** Shown when "Draft a response" is chosen without a scoped case — no backend call. */
export const DRAFT_REPLY_NEEDS_CASE_MESSAGE =
  'Draft replies must be tied to one case. Open a complaint from Cases, then use '
  + '**Draft reply to applicant** on that case — or use **Ask AI about this case** first so '
  + 'this assistant is scoped to that case, then choose Draft again.';

export const DRAFT_REPLY_DEFAULT_QUESTION = 'Draft an official reply to the applicant for this case.';

function newChatSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ask-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readStoredSessionId(userKey: string | null): string | null {
  if (!userKey) return null;
  try {
    return localStorage.getItem(sessionKeyForUser(userKey))
      || sessionStorage.getItem(sessionKeyForUser(userKey));
  } catch {
    return null;
  }
}

function writeStoredSessionId(userKey: string | null, id: string): void {
  if (!userKey) return;
  try {
    const key = sessionKeyForUser(userKey);
    localStorage.setItem(key, id);
    sessionStorage.setItem(key, id);
  } catch {
    /* private mode / blocked storage — in-memory signal still works */
  }
}

function readCachedTurns(userKey: string | null, sessionId: string): ChatTurn[] {
  if (!userKey) return [];
  try {
    const raw = localStorage.getItem(turnsKeyForUser(userKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { sessionId?: string; userKey?: string; turns?: ChatTurn[] };
    if (
      !parsed
      || parsed.sessionId !== sessionId
      || (parsed.userKey != null && parsed.userKey !== userKey)
      || !Array.isArray(parsed.turns)
    ) {
      return [];
    }
    return parsed.turns.filter((t) => t && (t.que || t.ans));
  } catch {
    return [];
  }
}

function writeCachedTurns(userKey: string | null, sessionId: string, turns: ChatTurn[]): void {
  if (!userKey) return;
  try {
    localStorage.setItem(
      turnsKeyForUser(userKey),
      JSON.stringify({ userKey, sessionId, turns }),
    );
  } catch {
    /* ignore */
  }
}

function clearCachedTurns(userKey: string | null): void {
  if (!userKey) return;
  try {
    localStorage.removeItem(turnsKeyForUser(userKey));
  } catch {
    /* ignore */
  }
}

/** Build citation stubs from {{CASE:id}} markers so restored bubbles still show cards. */
export function citationsFromAnswerText(text: string): AskCitation[] {
  const ids = new Set<number>();
  const re = new RegExp(CASE_MARKER_RE);
  let match: RegExpExecArray | null;
  while ((match = re.exec(String(text || ''))) !== null) {
    for (const part of match[1].split(',')) {
      const id = Number(part.trim());
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
  }
  return [...ids].map((grievanceId) => ({ grievanceId, subject: `Case ${grievanceId}` }));
}

/** Map DB conversation_json [{que,ans,feedback?}] into chat bubbles. */
export function turnsToChatMessages(
  turns: Array<{ que?: string; ans?: string; feedback?: AskFeedback }>,
): ChatMessage[] {
  const out: ChatMessage[] = [];
  let id = 1;
  let turnIndex = 0;
  for (const t of turns || []) {
    const que = String(t?.que ?? '').trim();
    const ans = String(t?.ans ?? '').trim();
    if (que) out.push({ id: id++, role: 'user', text: que });
    if (ans) {
      out.push({
        id: id++,
        role: 'assistant',
        text: ans,
        citations: citationsFromAnswerText(ans),
        turnIndex,
        feedback: t?.feedback ?? null,
      });
    }
    turnIndex++;
  }
  return out;
}

/** Pair user/assistant bubbles back into [{que,ans}] for local restore. */
export function messagesToTurns(messages: ChatMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role !== 'user') continue;
    const next = messages[i + 1];
    if (next?.role === 'assistant' && !next.isError) {
      turns.push({ que: m.text, ans: next.text });
    }
  }
  return turns;
}

/**
 * Single source of truth for the case-assistant conversation — shared by the floating
 * widget and the full-page assistant. Session id + local turns cache are keyed by the
 * logged-in user so one browser never reuses another account's Ask AI thread.
 */
@Injectable({ providedIn: 'root' })
export class ChatWidgetService {
  private readonly ocr = inject(DocumentOcrService);
  private readonly auth = inject(AuthService);
  private readonly rbac = inject(RbacService);

  readonly isOpen = signal(false);
  readonly scopedCaseId = signal<number | string | null>(null);
  /** Bumped when the composer should receive focus (e.g. after "Ask AI about this case"). */
  readonly focusComposerTick = signal(0);

  readonly query = signal('');
  readonly asking = signal(false);
  readonly messages = signal<ChatMessage[]>([]);
  readonly restoring = signal(true);
  /**
   * Groups Q&A turns in ai_conversations — persisted per-user in localStorage so refresh
   * keeps the same DB row for that officer only.
   */
  readonly sessionId = signal<string>('');

  /** Stable key for the signed-in desk user (null when logged out). */
  private readonly storageUserKey = computed(() => {
    if (!this.auth.isAuthenticated()) return null;
    const officer = this.auth.officer();
    if (!officer?.id) return null;
    return String(officer.id);
  });

  /**
   * Chat list (Ask AI redesign) — the full-page assistant's sidebar. Kept on this same
   * service, not a separate one, because "which chat is active" (sessionId/messages above)
   * and "what's in the list" are two views onto the same underlying conversations and need
   * to stay in lockstep — e.g. renaming the active chat must update its own sidebar row too.
   */
  readonly conversations = signal<AskConversationSummary[]>([]);
  readonly conversationsLoading = signal(false);
  readonly showingArchived = signal(false);

  /**
   * Language handling (Ask AI redesign). typeInMode only ever drives the composer's own
   * transliteration-suggestion UI (see assistant.component.ts) — it never reaches the
   * backend, since it's about how the officer types, not what gets asked. replyInMode does
   * reach the backend (see ask() below) — it overrides rag-service.js's
   * default "answer in the question's own language" behavior.
   */
  readonly typeInMode = signal<'auto' | 'hindi' | 'english'>('auto');
  readonly replyInMode = signal<'auto' | 'hindi' | 'english'>('auto');

  readonly suggestions = computed(() =>
    this.scopedCaseId() != null
      ? ['What is the applicant asking for?', 'Who is this case forwarded to?', 'Summarize this case in one line']
      : ['Which cases involve a land dispute?', 'Summarize the most recent case', 'List cases from Deoria tehsil'],
  );

  constructor() {
    let prevUserKey: string | null | undefined;
    let prevScope: number | string | null | undefined;
    let booted = false;

    effect(() => {
      const userKey = this.storageUserKey();
      const caseId = this.scopedCaseId();

      if (!booted) {
        booted = true;
        prevUserKey = userKey;
        prevScope = caseId;
        // Drop pre-fix global keys once so a prior user's thread cannot hydrate.
        try {
          localStorage.removeItem(ASK_AI_SESSION_KEY_LEGACY);
          localStorage.removeItem(ASK_AI_TURNS_KEY_LEGACY);
          sessionStorage.removeItem(ASK_AI_SESSION_KEY_LEGACY);
        } catch {
          /* ignore */
        }
        void this.bindUserAndHydrate(userKey, caseId);
        return;
      }

      if (userKey !== prevUserKey) {
        prevUserKey = userKey;
        prevScope = caseId;
        // Account switch / logout: never keep the previous user's bubbles or session id.
        this.resetForUserChange(userKey, caseId);
        return;
      }

      if (caseId === prevScope) return;
      const previous = prevScope;
      prevScope = caseId;
      // Entering / switching a case starts a fresh thread. Clearing scope back to
      // "all cases" keeps the current thread (and its sessionId) so refresh works.
      if (caseId != null && caseId !== previous) {
        this.rotateSession(userKey);
        this.messages.set([this.greetingFor(caseId)]);
      }
    });
  }

  private greetingFor(caseId: number | string | null): ChatMessage {
    return {
      id: 1,
      role: 'assistant',
      text:
        caseId != null
          ? 'Asking about this case only.'
          : 'Ask about any confirmed case — a name, village, or topic.',
    };
  }

  private resetForUserChange(userKey: string | null, caseId: number | string | null): void {
    this.query.set('');
    this.asking.set(false);
    this.isOpen.set(false);
    if (!userKey) {
      this.sessionId.set('');
      this.messages.set([]);
      this.restoring.set(false);
      return;
    }
    void this.bindUserAndHydrate(userKey, caseId);
  }

  private async bindUserAndHydrate(
    userKey: string | null,
    caseId: number | string | null,
  ): Promise<void> {
    if (!userKey) {
      this.sessionId.set('');
      this.messages.set([]);
      this.restoring.set(false);
      return;
    }
    const stored = readStoredSessionId(userKey);
    const sid = stored || newChatSessionId();
    writeStoredSessionId(userKey, sid);
    this.sessionId.set(sid);
    await this.hydrateOrGreet(userKey, caseId);
  }

  private async hydrateOrGreet(
    userKey: string | null,
    caseId: number | string | null,
  ): Promise<void> {
    this.restoring.set(true);
    try {
      await this.auth.whenSessionReady();
      // Re-check after await — user may have logged out mid-flight.
      const liveKey = this.storageUserKey();
      if (!liveKey || liveKey !== userKey) {
        this.messages.set([]);
        return;
      }

      const sid = this.sessionId() || readStoredSessionId(liveKey);
      if (sid) {
        if (sid !== this.sessionId()) this.sessionId.set(sid);
        writeStoredSessionId(liveKey, sid);

        let turns: ChatTurn[] = [];
        if (this.auth.isAuthenticated() && this.rbac.hasPermission('ASSISTANT_READ')) {
          try {
            const row = await this.ocr.getAskConversation(sid);
            // Another user's session id must never hydrate (API returns 403 or empty).
            if (Array.isArray(row?.conversationJson) && row.conversationJson.length) {
              turns = row.conversationJson.map((t) => ({
                que: String(t?.que ?? ''),
                ans: String(t?.ans ?? ''),
              }));
            }
          } catch {
            // Stale/foreign session id in storage — mint a fresh one for this user.
            this.rotateSession(liveKey);
            turns = [];
          }
        }

        if (!turns.length) {
          turns = readCachedTurns(liveKey, sid);
        }

        if (turns.length) {
          writeCachedTurns(liveKey, sid, turns);
          this.messages.set(turnsToChatMessages(turns));
          return;
        }
      }
      this.messages.set([this.greetingFor(caseId)]);
    } catch {
      this.messages.set([this.greetingFor(caseId)]);
    } finally {
      this.restoring.set(false);
    }
  }

  private rotateSession(userKey: string | null = this.storageUserKey()): void {
    const id = newChatSessionId();
    writeStoredSessionId(userKey, id);
    this.sessionId.set(id);
    clearCachedTurns(userKey);
  }

  private persistMessagesToCache(): void {
    const userKey = this.storageUserKey();
    const sid = this.sessionId();
    if (!userKey || !sid) return;
    const turns = messagesToTurns(this.messages());
    if (turns.length) writeCachedTurns(userKey, sid, turns);
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
  }

  /** Opens the widget without changing whatever scope it already has. */
  openGlobal(): void {
    this.isOpen.set(true);
  }

  /**
   * Opens the widget scoped to one case — used by "Ask AI about this case".
   * Optional `draft` is prefilled in the composer so the officer can press Enter.
   */
  openForCase(caseId: number | string, draft?: string): void {
    this.scopedCaseId.set(caseId);
    if (draft?.trim()) this.query.set(draft.trim());
    this.isOpen.set(true);
    this.focusComposerTick.update((n) => n + 1);
  }

  close(): void {
    this.isOpen.set(false);
  }

  clearScope(): void {
    this.scopedCaseId.set(null);
  }

  /**
   * Welcome-screen "Draft a response" — scoped case runs draft_reply; otherwise guides locally.
   */
  async handleDraftStarter(userLabel = 'Draft a reply to the applicant'): Promise<void> {
    if (this.asking() || this.restoring()) return;
    const caseId = this.scopedCaseId();
    if (caseId == null) {
      this.guideDraftNeedsCase(userLabel);
      return;
    }
    await this.askDraftForCase(caseId, userLabel);
  }

  /** Official letter draft for one case (backend intent draft_reply). */
  async askDraftForCase(caseId: number | string, question?: string): Promise<void> {
    this.scopedCaseId.set(caseId);
    const text = (question ?? '').trim() || DRAFT_REPLY_DEFAULT_QUESTION;
    await this.ask(text, { intent: 'draft_reply', grievanceId: caseId });
  }

  private guideDraftNeedsCase(userLabel: string): void {
    const text = userLabel.trim() || 'Draft a reply to the applicant';
    this.messages.update((messages) => [
      ...messages,
      { id: Date.now(), role: 'user', text },
      {
        id: Date.now() + 1,
        role: 'assistant',
        text: DRAFT_REPLY_NEEDS_CASE_MESSAGE.replace(/\*\*(.+?)\*\*/g, '$1'),
      },
    ]);
  }

  async ask(
    question = this.query(),
    options?: { intent?: 'draft_reply'; grievanceId?: number | string },
  ): Promise<void> {
    const intent = options?.intent;
    const text = question.trim();
    const isDraft = intent === 'draft_reply';
    if ((!text && !isDraft) || this.asking() || this.restoring()) return;

    const userKey = this.storageUserKey();
    if (!userKey) return;

    const grievanceId = isDraft
      ? (options?.grievanceId ?? this.scopedCaseId())
      : (this.scopedCaseId() ?? undefined);
    if (isDraft && (grievanceId == null || grievanceId === '')) {
      this.guideDraftNeedsCase(text || 'Draft a reply to the applicant');
      return;
    }

    const promptText = text || DRAFT_REPLY_DEFAULT_QUESTION;

    // Re-read storage in case another tab rotated; never drop an empty sessionId.
    const sid = this.sessionId() || readStoredSessionId(userKey) || newChatSessionId();
    if (sid !== this.sessionId()) this.sessionId.set(sid);
    writeStoredSessionId(userKey, sid);

    this.messages.update((messages) => [...messages, { id: Date.now(), role: 'user', text: promptText }]);
    this.query.set('');
    this.asking.set(true);

    try {
      // Mirrors how many turns actually landed in conversation_json so far. Counting by
      // turnIndex presence (not just "role === assistant") matters: the greeting bubble is
      // also an assistant message but was never logged to the DB, so it must never be
      // counted — same for any error bubble. Only messages that themselves carry a real
      // turnIndex (restored from the DB, or appended here after a prior success) count.
      const turnIndex = this.messages().filter((m) => m.turnIndex != null).length;
      const result = await this.ocr.ask(
        promptText,
        grievanceId ?? undefined,
        sid,
        this.resolveReplyLanguage(),
        intent,
      );
      this.messages.update((messages) => [
        ...messages,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: result.answer,
          citations: result.citations,
          turnIndex,
          feedback: null,
        },
      ]);
      this.persistMessagesToCache();
      // Cheap DB list query (no Gemini call) — keeps the sidebar's title/ordering fresh,
      // and is how a brand-new chat (this was its first message) appears in the list at all.
      void this.loadConversations();
    } catch (err) {
      this.messages.update((messages) => [
        ...messages,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: userFacingMessage(err),
          isError: true,
        },
      ]);
    } finally {
      this.asking.set(false);
    }
  }

  clearChat(): void {
    this.rotateSession();
    this.messages.set([this.greetingFor(this.scopedCaseId())]);
  }

  // ── Chat list (Ask AI redesign) ──────────────────────────────────────────

  async loadConversations(archived = this.showingArchived()): Promise<void> {
    if (!this.auth.isAuthenticated() || !this.rbac.hasPermission('ASSISTANT_READ')) {
      this.conversations.set([]);
      return;
    }
    this.conversationsLoading.set(true);
    try {
      this.conversations.set(await this.ocr.listAskConversations(archived));
    } catch {
      this.conversations.set([]);
    } finally {
      this.conversationsLoading.set(false);
    }
  }

  async setShowingArchived(archived: boolean): Promise<void> {
    if (this.showingArchived() === archived) return;
    this.showingArchived.set(archived);
    await this.loadConversations(archived);
  }

  /** Switches the active thread to an existing chat and restores its history. */
  async selectConversation(sessionId: string): Promise<void> {
    const sid = String(sessionId || '').trim();
    if (!sid || (sid === this.sessionId() && !this.restoring())) return;
    const userKey = this.storageUserKey();
    this.scopedCaseId.set(null);
    this.sessionId.set(sid);
    writeStoredSessionId(userKey, sid);
    this.restoring.set(true);
    try {
      const row = await this.ocr.getAskConversation(sid);
      const turns = row?.conversationJson ?? [];
      writeCachedTurns(userKey, sid, turns);
      this.messages.set(turns.length ? turnsToChatMessages(turns) : [this.greetingFor(null)]);
    } catch {
      this.messages.set([this.greetingFor(null)]);
    } finally {
      this.restoring.set(false);
    }
  }

  /**
   * Starts a genuinely new, empty thread. The conversation row itself is only created
   * server-side lazily, on the first ask() call that uses this session id (same as it always
   * has been) — this just points the UI at a fresh id so that first message lands in a new
   * chat instead of appending to whatever was open before.
   */
  startNewChat(): void {
    this.scopedCaseId.set(null);
    this.rotateSession();
    this.messages.set([this.greetingFor(null)]);
  }

  async renameConversation(sessionId: string, title: string): Promise<void> {
    const trimmed = title.trim();
    if (!trimmed) return;
    await this.ocr.patchAskConversation(sessionId, { title: trimmed });
    await this.loadConversations();
  }

  async togglePin(conversation: AskConversationSummary): Promise<void> {
    await this.ocr.patchAskConversation(conversation.sessionId, { pinned: !conversation.pinnedAt });
    await this.loadConversations();
  }

  async toggleArchive(conversation: AskConversationSummary): Promise<void> {
    await this.ocr.patchAskConversation(conversation.sessionId, { archived: !conversation.archivedAt });
    await this.loadConversations();
  }

  /** Empties Q&A history for one chat — keeps its title/pin/archive state, distinct from delete. */
  async clearConversationMessages(sessionId: string): Promise<void> {
    await this.ocr.clearAskConversation(sessionId);
    if (sessionId === this.sessionId()) {
      this.messages.set([this.greetingFor(this.scopedCaseId())]);
      clearCachedTurns(this.storageUserKey());
    }
    await this.loadConversations();
  }

  async deleteConversation(sessionId: string): Promise<void> {
    await this.ocr.deleteAskConversation(sessionId);
    if (sessionId === this.sessionId()) {
      this.startNewChat();
    }
    await this.loadConversations();
  }

  // ── Message-level actions ────────────────────────────────────────────────

  /** 'auto' means "let the model mirror the question's language" — the existing default
   * behavior — so it's never sent to the backend as an actual override value. */
  private resolveReplyLanguage(): 'hindi' | 'english' | undefined {
    const mode = this.replyInMode();
    return mode === 'hindi' || mode === 'english' ? mode : undefined;
  }

  /** Toggles thumbs up/down on one message — clicking the same one again clears it. */
  async setFeedback(message: ChatMessage, feedback: AskFeedback): Promise<void> {
    const sid = this.sessionId();
    if (!sid || message.turnIndex == null) return;
    const previous = message.feedback ?? null;
    const next: AskFeedback | null = previous === feedback ? null : feedback;

    this.messages.update((list) => list.map((m) => (m.id === message.id ? { ...m, feedback: next } : m)));
    try {
      await this.ocr.sendAskFeedback(sid, message.turnIndex, next);
    } catch {
      this.messages.update((list) => list.map((m) => (m.id === message.id ? { ...m, feedback: previous } : m)));
    }
  }
}
